import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  House, 
  BookOpen, 
  Exam, 
  RoadHorizon, 
  Bookmark,
  Gear,
  CaretCircleLeft,
  List,
  Brain,
  Medal,
  DotsThree,
  SignOut,
  X,
  Crown,
  Sword,
  Book
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/dashboard', icon: House, label: 'Home' },
  { path: '/smart-review', icon: Brain, label: 'Review' },
  { path: '/practice', icon: BookOpen, label: 'Practice' },
  { path: '/mock-exam', icon: Exam, label: 'Exam' },
];

const sidebarItems = [
  { path: '/dashboard', icon: House, label: 'Dashboard' },
  { path: '/smart-review', icon: Brain, label: 'Smart Review' },
  { path: '/practice', icon: BookOpen, label: 'Practice Quiz' },
  { path: '/mock-exam', icon: Exam, label: 'Mock Exam' },
  { path: '/highway-code', icon: Book, label: 'Highway Code' },
  { path: '/challenge', icon: Sword, label: 'Challenge' },
  { path: '/achievements', icon: Medal, label: 'Achievements' },
  { path: '/hazard-perception', icon: CaretCircleLeft, label: 'Hazard Perception' },
  { path: '/road-signs', icon: RoadHorizon, label: 'Road Signs' },
  { path: '/bookmarks', icon: Bookmark, label: 'Bookmarks' },
  { path: '/settings', icon: Gear, label: 'Settings' },
];

const moreMenuItems = [
  { path: '/highway-code', icon: Book, label: 'Highway Code' },
  { path: '/challenge', icon: Sword, label: 'Challenge' },
  { path: '/achievements', icon: Medal, label: 'Achievements' },
  { path: '/bookmarks', icon: Bookmark, label: 'Bookmarks' },
  { path: '/hazard-perception', icon: CaretCircleLeft, label: 'Hazard Perception' },
  { path: '/road-signs', icon: RoadHorizon, label: 'Road Signs' },
  { path: '/settings', icon: Gear, label: 'Settings' },
];

export const BottomNav = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showMore, setShowMore] = useState(false);
  
  // Hide on auth pages and during quiz/exam
  const hiddenPaths = ['/login', '/register', '/quiz', '/exam'];
  if (hiddenPaths.some(path => location.pathname.startsWith(path))) {
    return null;
  }

  return (
    <>
      {/* More Menu Overlay */}
      {showMore && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="more-menu-overlay">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMore(false)} 
          />
          <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border rounded-t-2xl p-4 pb-6 animate-in slide-in-from-bottom duration-200">
            {/* Handle bar */}
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-4" />
            
            {/* User info */}
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-lg">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowMore(false)}
                className="p-2 text-muted-foreground hover:text-foreground"
                data-testid="close-more-menu"
              >
                <X weight="bold" className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="space-y-1 mb-4">
              {moreMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowMore(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-sm transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground hover:bg-muted"
                    )}
                    data-testid={`more-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon weight={isActive ? "fill" : "regular"} className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Logout */}
            <button
              onClick={() => { setShowMore(false); logout(); }}
              className="flex items-center gap-3 px-4 py-3 w-full text-destructive hover:bg-destructive/10 rounded-sm transition-colors"
              data-testid="mobile-logout-button"
            >
              <SignOut weight="regular" className="w-5 h-5" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="bottom-nav md:hidden" data-testid="bottom-navigation">
        <div className="flex items-center justify-around py-2 px-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center gap-1 p-2 min-w-[56px] transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon weight={isActive ? "fill" : "regular"} className="w-6 h-6" />
                <span className="text-[10px] font-medium uppercase tracking-wider">{item.label}</span>
              </NavLink>
            );
          })}
          <button
            onClick={() => setShowMore(true)}
            className={cn(
              "flex flex-col items-center gap-1 p-2 min-w-[56px] transition-colors",
              showMore ? "text-primary" : "text-muted-foreground hover:text-foreground"
            )}
            data-testid="nav-more"
          >
            <DotsThree weight="bold" className="w-6 h-6" />
            <span className="text-[10px] font-medium uppercase tracking-wider">More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export const Sidebar = ({ collapsed, onToggle }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Hide on auth pages
  if (['/login', '/register'].includes(location.pathname)) {
    return null;
  }

  return (
    <aside 
      className={cn(
        "hidden md:flex flex-col h-screen bg-card border-r border-border fixed left-0 top-0 z-40 transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        {!collapsed && (
          <h1 className="font-heading font-black text-xl tracking-tight text-primary">
            TestReady
          </h1>
        )}
        <button
          onClick={onToggle}
          className="p-2 hover:bg-muted rounded-sm transition-colors"
          data-testid="sidebar-toggle"
        >
          <List weight="bold" className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  data-testid={`sidebar-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon weight={isActive ? "fill" : "regular"} className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && (
                    <span className="font-medium text-sm">{item.label}</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-border">
        {!collapsed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold text-lg">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
              data-testid="logout-button"
            >
              Sign Out
            </button>
          </div>
        ) : (
          <button
            onClick={logout}
            className="w-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
            data-testid="logout-button-collapsed"
          >
            <CaretCircleLeft weight="regular" className="w-5 h-5 mx-auto" />
          </button>
        )}
      </div>
    </aside>
  );
};

export const Layout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const location = useLocation();
  
  const isAuthPage = ['/login', '/register'].includes(location.pathname);
  const isFullScreenPage = location.pathname.startsWith('/quiz') || location.pathname.startsWith('/exam');

  if (isAuthPage || isFullScreenPage) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main 
        className={cn(
          "min-h-screen pb-20 md:pb-0 transition-all duration-300",
          sidebarCollapsed ? "md:ml-16" : "md:ml-64"
        )}
      >
        {children}
      </main>
      <BottomNav />
    </div>
  );
};
